import type { CSSProperties } from 'react';
import { CLIP_TAG_COLORS, type ClipTagColor } from '@proref/schemas';

// Maps a tag's palette KEY (stored on clip_tags.color, one of CLIP_TAG_COLORS) to a swatch hex.
// Rendering lives here — not in the DB — so the palette stays theme-consistent and swappable. Keep
// the keys in sync with CLIP_TAG_COLORS (@proref/schemas) and the DB CHECK on clip_tags.color.
const TAG_COLOR_HEX: Record<ClipTagColor, string> = {
  slate: '#64748b',
  red: '#ef4444',
  amber: '#f59e0b',
  green: '#22c55e',
  teal: '#14b8a6',
  blue: '#3b82f6',
  violet: '#8b5cf6',
  pink: '#ec4899',
};

const DEFAULT_COLOR: ClipTagColor = 'slate';

// The ordered palette for the swatch picker.
export const TAG_COLOR_OPTIONS: { key: ClipTagColor; hex: string }[] = CLIP_TAG_COLORS.map((key) => ({
  key,
  hex: TAG_COLOR_HEX[key],
}));

// Resolve a stored colour key (possibly undefined on a pre-colour tag, or an unknown value) to a hex,
// falling back to the neutral default so a chip always renders something sensible.
export function tagHex(color: string | undefined): string {
  return (color && TAG_COLOR_HEX[color as ClipTagColor]) || TAG_COLOR_HEX[DEFAULT_COLOR];
}

// Dark vs light text for a solid swatch background — relative luminance so a light chip (amber) gets
// dark text and a dark chip (violet) gets white.
function contrastText(hex: string): string {
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
  return luminance > 0.62 ? '#1a1a1a' : '#ffffff';
}

// Interactive chip style (editor vocabulary, filter chips). Active/applied → a solid swatch with
// contrast text; inactive/available → an outline (colour border + colour text, transparent fill) so
// it reads as "not yet applied" while still showing its colour. Inline styles (not Tailwind classes)
// keep arbitrary swatch hexes out of the purge and consistent across themes.
export function tagChipStyle(color: string | undefined, active: boolean): CSSProperties {
  const hex = tagHex(color);
  if (active) return { backgroundColor: hex, color: contrastText(hex), borderColor: hex };
  // `${hex}66` ≈ 40% alpha border; transparent fill so the surface shows through.
  return { backgroundColor: 'transparent', color: hex, borderColor: `${hex}66` };
}

// Read-only chip style (clip cards) — a soft tint so the chip is legible but quiet. `${hex}1f` ≈ 12%
// alpha fill; the label takes the full swatch colour.
export function tagSoftStyle(color: string | undefined): CSSProperties {
  const hex = tagHex(color);
  return { backgroundColor: `${hex}1f`, color: hex };
}
